// formatPhone — return `🇨🇺 +53 5 555 5555` (flag + spaced E.164) for display.
//
// Detect country from the E.164 prefix. NANP (+1) is split into US / Canada by
// area code — Canadian area codes from the public NANPA list, anything else
// falls back to 🇺🇸. Unknown country codes fall back to 🌐.
//
// Spacing rules per ISO/CEPT recommendations:
//   +53 → "+53 5 555 5555"   (Cuba: country + 1 + 3 + 4)
//   +1  → "+1 555 555 5555"  (NANP: country + 3 + 3 + 4)
//   +49 → "+49 30 123 4567"  (general: country + first 2 + chunks of 3/4)
//   default → "+XX XX XXX XXXX" — greedy chunking that's always readable.

// Canadian area codes (NANPA — current list). Anything in +1 NOT in this set
// is treated as US. We don't try to distinguish other NANP countries (PR/DR/
// Jamaica etc) — they'd otherwise need a much larger table.
const CA_AREA_CODES = new Set([
  "204", "226", "236", "249", "250", "263", "289", "306", "343", "354", "365",
  "367", "368", "382", "403", "416", "418", "428", "431", "437", "438", "450",
  "468", "474", "506", "514", "519", "548", "579", "581", "584", "587", "604",
  "613", "639", "647", "672", "683", "705", "709", "742", "753", "778", "780",
  "782", "807", "819", "825", "867", "873", "879", "902", "905",
]);

const COUNTRY_TABLE = [
  { prefix: "+53", flag: "🇨🇺", iso: "CU", split: [1, 3, 4] },         // Cuba 8 digits
  { prefix: "+7",  flag: "🇷🇺", iso: "RU", split: [3, 3, 2, 2] },      // Russia / KZ
  { prefix: "+49", flag: "🇩🇪", iso: "DE", split: [3, 7] },            // Germany variable
  { prefix: "+33", flag: "🇫🇷", iso: "FR", split: [1, 2, 2, 2, 2] },   // France
  { prefix: "+39", flag: "🇮🇹", iso: "IT", split: [3, 7] },            // Italy
  { prefix: "+34", flag: "🇪🇸", iso: "ES", split: [3, 3, 3] },         // Spain
  { prefix: "+52", flag: "🇲🇽", iso: "MX", split: [2, 4, 4] },         // Mexico
  { prefix: "+44", flag: "🇬🇧", iso: "GB", split: [4, 6] },            // UK
  { prefix: "+54", flag: "🇦🇷", iso: "AR", split: [2, 4, 4] },         // Argentina
];

function chunk(digits, sizes) {
  const out = [];
  let i = 0;
  for (const s of sizes) {
    if (i >= digits.length) break;
    out.push(digits.slice(i, i + s));
    i += s;
  }
  // Anything leftover goes into a trailing chunk
  if (i < digits.length) out.push(digits.slice(i));
  return out.filter(Boolean).join(" ");
}

function defaultChunk(digits) {
  // Greedy 3-3-4 if short, else 3-3-3-... — always readable.
  if (digits.length <= 7) return chunk(digits, [3, 4]);
  if (digits.length <= 10) return chunk(digits, [3, 3, 4]);
  return chunk(digits, [3, 3, 3, 4, 4]);
}

function detectNanpFlag(digitsAfterCode) {
  const area = digitsAfterCode.slice(0, 3);
  return CA_AREA_CODES.has(area) ? { flag: "🇨🇦", iso: "CA" } : { flag: "🇺🇸", iso: "US" };
}

/**
 * Returns { flag, iso, formatted } for the given E.164 string.
 * Examples:
 *   "+5358151234"  → { flag: "🇨🇺", formatted: "+53 5 815 1234" }
 *   "+14165551212" → { flag: "🇨🇦", formatted: "+1 416 555 1212" }
 *   "+15555551212" → { flag: "🇺🇸", formatted: "+1 555 555 1212" }
 */
export function parsePhone(input) {
  if (!input) return { flag: "🌐", iso: null, formatted: "" };
  let trimmed = String(input).replace(/\s+/g, "").replace(/[^\d+]/g, "");
  if (!trimmed) return { flag: "🌐", iso: null, formatted: "" };
  // Normalise: 00-prefixed international → +; bare digits with a recognisable
  // country prefix → +<prefix>. This lets stored numbers like "5378322222"
  // (Cuba) still resolve to 🇨🇺 +53 7 832 2222 instead of falling back to 🌐.
  if (trimmed.startsWith("00")) trimmed = "+" + trimmed.slice(2);
  if (!trimmed.startsWith("+")) {
    // Try matching each known prefix without the leading '+'.
    const NANP_LEN = 11;
    if (trimmed.length === NANP_LEN && trimmed.startsWith("1")) {
      trimmed = "+" + trimmed;
    } else {
      const guess = COUNTRY_TABLE.find((c) => trimmed.startsWith(c.prefix.slice(1)));
      if (guess) trimmed = "+" + trimmed;
      else return { flag: "🌐", iso: null, formatted: trimmed };
    }
  }

  // NANP: +1 is special-cased first.
  if (trimmed.startsWith("+1") && trimmed.length >= 12) {
    const digits = trimmed.slice(2);
    const { flag, iso } = detectNanpFlag(digits);
    return { flag, iso, formatted: `+1 ${chunk(digits, [3, 3, 4])}` };
  }

  for (const c of COUNTRY_TABLE) {
    if (trimmed.startsWith(c.prefix)) {
      const digits = trimmed.slice(c.prefix.length);
      return { flag: c.flag, iso: c.iso, formatted: `${c.prefix} ${chunk(digits, c.split)}` };
    }
  }

  // Unknown country — split off the most common 1-3 digit country codes.
  const m = trimmed.match(/^\+(\d{1,3})(\d+)$/);
  if (m) {
    return { flag: "🌐", iso: null, formatted: `+${m[1]} ${defaultChunk(m[2])}` };
  }
  return { flag: "🌐", iso: null, formatted: trimmed };
}

/** `🇨🇺 +53 5 815 1234` */
export function formatPhoneWithFlag(input) {
  if (!input) return "";
  const { flag, formatted } = parsePhone(input);
  if (!formatted) return "";
  return `${flag} ${formatted}`.trim();
}

/** Same as formatPhoneWithFlag but without the flag, for places where the flag
 *  is already shown elsewhere (e.g., next to a country selector).
 */
export function formatPhone(input) {
  return parsePhone(input).formatted;
}
